import { ReactElement, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { Grid, H } from '@farewill/ui'

import { FuneralPlanLeadAttributes } from 'lib/models/lead'
import RadioGroup from 'components/form/radio-group'
import { AddressInput, Input } from 'components/form'
import { YES_NO_OPTIONS } from 'utils/enums'
import { getYearsElapsed } from 'utils/helpers'

const FORM_FIELDS_NAMES = {
  BUYING_FOR_THEMSELVES: 'purchaserIsCoveredIndividual',
  NOMINATED_REPRESENTATIVE: 'hasNominatedRepresentative',
  NOMINATED_REPRESENTATIVE_CONTACT: 'nominatedRepresentative',
  NOMINATED_REPRESENTATIVE_CALLER: 'purchaserIsNominatedRepresentative',
} as const

const NominatedRepresentativeContactDetails = (): ReactElement => {
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<FuneralPlanLeadAttributes>()

  const cannotBeContactedDirectly =
    values.quoteAttributes[FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT]
      ?.canBeContactedDirectly === 'false'

  useEffect(() => {
    if (cannotBeContactedDirectly) {
      setFieldValue(
        `quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.addressFields`,
        {
          lineOne: null,
          lineTwo: null,
          city: null,
          countryCode: null,
          postalCode: null,
        }
      )
      handleSubmit()
    }
  }, [handleSubmit, setFieldValue, cannotBeContactedDirectly])

  return (
    <Grid.Item>
      <Grid style={{ gridAutoFlow: 'row' }}>
        <Grid.Item spanFromL={6} startColumn={1}>
          <Input
            label="First name"
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.firstName`}
            handleSave={() => handleSubmit()}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Input
            label="Last name"
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.lastName`}
            handleSave={() => handleSubmit()}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Input
            label="Phone number"
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.phoneNumber`}
            handleSave={() => handleSubmit()}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Input
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.email`}
            label="Email"
            type="email"
            handleSave={() => handleSubmit()}
            allowCopyToClipboard
          />
        </Grid.Item>
        <Grid.Item spanFromL={6}>
          <Input
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.dateOfBirth`}
            label="Date of birth"
            type="date"
            handleSave={() => handleSubmit()}
            hint={
              values.quoteAttributes.nominatedRepresentative?.dateOfBirth
                ? `Age: ${getYearsElapsed({
                    dateOfBirth:
                      values.quoteAttributes.nominatedRepresentative
                        ?.dateOfBirth,
                  })}`
                : undefined
            }
          />
        </Grid.Item>
        <Grid.Item>
          <RadioGroup
            label="Can we contact this person directly?"
            hint="This must be Yes for new plans, it is left in here for Legacy plan that had it set to No"
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.canBeContactedDirectly`}
            options={[
              { value: 'true', label: 'Yes' },
              {
                value: 'false',
                label: 'No',
                isOptionDisabled: !(
                  values.quoteAttributes.nominatedRepresentative
                    ?.canBeContactedDirectly === 'false'
                ),
              },
            ]}
            handleSave={() => handleSubmit()}
          />
        </Grid.Item>
        {values.quoteAttributes[
          FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT
        ]?.canBeContactedDirectly === 'true' && (
          <Grid.Item spanFromL={12}>
            <AddressInput
              label="Address"
              name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}`}
              onAddressChanged={(args) => {
                setFieldValue(
                  `quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}.addressFields`,
                  args
                )
                handleSubmit()
              }}
              addressValues={
                values.quoteAttributes[
                  FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT
                ]?.addressFields
              }
            />
          </Grid.Item>
        )}
      </Grid>
    </Grid.Item>
  )
}

const NominatedRepresentative = (): ReactElement => {
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<FuneralPlanLeadAttributes>()

  const purchaserIsCoveredIndividual =
    values.quoteAttributes[FORM_FIELDS_NAMES.BUYING_FOR_THEMSELVES] === 'true'

  const showNominatedRepresentativeContactFields =
    values.quoteAttributes[FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE] ===
    'true'

  const callerIsNotNominatedRepresentative =
    values.quoteAttributes[
      FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CALLER
    ] === 'false'

  const callerIsNominatedRepresentative =
    values.quoteAttributes[
      FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CALLER
    ] === 'true'

  /**
   * If the answer for "Do they want a nominated representative?" is changing from Yes to No,
   * we need to clear any added nominated representative contact details.
   * And if the caller is the nominated representative, we also need to clear the
   * purchaserIsNominatedRepresentative field.
   */
  const handleWantsNominatedRepresentativeChange = ({
    value,
  }: {
    value: string
  }) => {
    if (['false', 'none'].includes(value)) {
      const emptyNominatedRepresentative = {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        email: null,
        addressFields: {
          addressLine1: null,
          addressLine2: null,
          addressTown: null,
          addressCounty: null,
          addressPostcode: null,
        },
        canBeContactedDirectly: true,
      }
      setFieldValue(
        `quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CONTACT}`,
        emptyNominatedRepresentative
      )

      callerIsNominatedRepresentative &&
        setFieldValue(
          `quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CALLER}`,
          null
        )
    }

    handleSubmit()
  }

  return (
    <Grid.Item data-testid="nominated-representative">
      <H size="S" margin={['XL', 0, 'S']}>
        Nominated representative
      </H>
      <Grid data-testid="nominated-rep">
        <Grid.Item>
          <RadioGroup
            name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE}`}
            label="Do they want a nominated representative?"
            handleSave={handleWantsNominatedRepresentativeChange}
            options={YES_NO_OPTIONS}
          />
        </Grid.Item>
        {showNominatedRepresentativeContactFields &&
          (purchaserIsCoveredIndividual ? (
            <NominatedRepresentativeContactDetails />
          ) : (
            <>
              <Grid.Item>
                <RadioGroup
                  name={`quoteAttributes.${FORM_FIELDS_NAMES.NOMINATED_REPRESENTATIVE_CALLER}`}
                  label="Is the caller the nominated representative?"
                  options={YES_NO_OPTIONS}
                  handleSave={() => handleSubmit()}
                />
              </Grid.Item>
              {callerIsNotNominatedRepresentative && (
                <NominatedRepresentativeContactDetails />
              )}
            </>
          ))}
      </Grid>
    </Grid.Item>
  )
}

export default NominatedRepresentative
